import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiPlus, FiUpload, FiTrash2, FiEdit2, FiArrowLeft, FiDownload, FiCheck } from 'react-icons/fi';
import { questions as questionsApi, exams as examsApi } from '../../api';
import toast from 'react-hot-toast';

const DIFF_COLORS = { easy: 'text-emerald-600 bg-emerald-50', medium: 'text-amber-600 bg-amber-50', hard: 'text-red-600 bg-red-50' };
const TYPE_LABELS = { mcq: 'MCQ', true_false: 'T/F', multi_correct: 'Multi' };

const QuestionForm = ({ question, onSave, onCancel }) => {
  const [form, setForm] = useState(question || {
    question_text: '', question_type: 'mcq', option_a: '', option_b: '', option_c: '', option_d: '',
    correct_answer: 'a', explanation: '', category: '', difficulty: 'medium', marks: 1, negative_marks: 0,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.question_text.trim()) { toast.error('Question text is required'); return; }
    if (!form.correct_answer) { toast.error('Correct answer is required'); return; }
    onSave(form);
  };

  const isTF = form.question_type === 'true_false';

  return (
    <div className="card p-5 border-2 border-navy/20 space-y-4">
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="label">Question Type</label>
          <select className="input" value={form.question_type} onChange={e => {
            set('question_type', e.target.value);
            if (e.target.value === 'true_false') set('correct_answer', 'true');
            else set('correct_answer', 'a');
          }}>
            <option value="mcq">Multiple Choice (Single)</option>
            <option value="multi_correct">Multiple Choice (Multiple)</option>
            <option value="true_false">True / False</option>
          </select>
        </div>
        <div>
          <label className="label">Difficulty</label>
          <select className="input" value={form.difficulty} onChange={e => set('difficulty', e.target.value)}>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>
        <div>
          <label className="label">Marks</label>
          <input type="number" className="input w-20" min={0} step={0.5} value={form.marks} onChange={e => set('marks', parseFloat(e.target.value) || 0)} />
        </div>
      </div>

      <div>
        <label className="label">Question Text *</label>
        <textarea className="input resize-none" rows={3} placeholder="Enter your question here..."
          value={form.question_text} onChange={e => set('question_text', e.target.value)} />
      </div>

      {!isTF && (
        <div className="grid grid-cols-2 gap-3">
          {['a','b','c','d'].map(opt => (
            <div key={opt} className={`relative flex items-center gap-2 p-3 rounded-xl border-2 transition-colors ${form.correct_answer === opt || form.correct_answer?.includes?.(opt) ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200'}`}>
              <div className="flex items-center">
                {form.question_type === 'multi_correct' ? (
                  <input type="checkbox" className="w-4 h-4 accent-emerald-600" checked={form.correct_answer?.includes(opt)}
                    onChange={e => {
                      const current = (form.correct_answer || '').split(',').filter(Boolean);
                      const updated = e.target.checked ? [...current, opt].sort() : current.filter(c => c !== opt);
                      set('correct_answer', updated.join(','));
                    }} />
                ) : (
                  <input type="radio" name="correct" className="w-4 h-4 accent-emerald-600" checked={form.correct_answer === opt}
                    onChange={() => set('correct_answer', opt)} />
                )}
              </div>
              <span className="font-bold text-gray-500 text-sm uppercase w-5">{opt}.</span>
              <input className="flex-1 bg-transparent text-sm outline-none text-gray-700 placeholder:text-gray-300"
                placeholder={`Option ${opt.toUpperCase()}`} value={form[`option_${opt}`] || ''}
                onChange={e => set(`option_${opt}`, e.target.value)} />
              {(form.correct_answer === opt || form.correct_answer?.includes?.(opt)) && (
                <FiCheck size={14} className="text-emerald-600 flex-shrink-0" />
              )}
            </div>
          ))}
        </div>
      )}

      {isTF && (
        <div className="flex gap-3">
          {['true','false'].map(opt => (
            <label key={opt} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-colors capitalize font-semibold ${form.correct_answer === opt ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600'}`}>
              <input type="radio" name="tf" className="sr-only" checked={form.correct_answer === opt} onChange={() => set('correct_answer', opt)} />
              {opt}
            </label>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Category</label>
          <input className="input" placeholder="e.g. JavaScript, Aptitude" value={form.category} onChange={e => set('category', e.target.value)} />
        </div>
        <div>
          <label className="label">Explanation (optional)</label>
          <input className="input" placeholder="Why is this the correct answer?" value={form.explanation} onChange={e => set('explanation', e.target.value)} />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button type="button" onClick={onCancel} className="btn-secondary flex-1">Cancel</button>
        <button type="button" onClick={handleSave} className="btn-primary flex-1">
          <FiCheck size={15} /> Save Question
        </button>
      </div>
    </div>
  );
};

export default function QuestionManagerPage() {
  const { id: examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingQ, setEditingQ] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    Promise.all([examsApi.getOne(examId), questionsApi.getByExam(examId)])
      .then(([eRes, qRes]) => { setExam(eRes.data.data); setQuestions(qRes.data.data || []); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [examId]);

  const handleAdd = async (form) => {
    try {
      const res = await questionsApi.add(examId, form);
      setQuestions(q => [...q, res.data.data]);
      setShowForm(false);
      toast.success('Question added');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleUpdate = async (form) => {
    try {
      const res = await questionsApi.update(editingQ.id, form);
      setQuestions(q => q.map(x => x.id === editingQ.id ? res.data.data : x));
      setEditingQ(null);
      toast.success('Question updated');
    } catch { toast.error('Update failed'); }
  };

  const handleDelete = async (qId) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await questionsApi.delete(qId);
      setQuestions(q => q.filter(x => x.id !== qId));
      toast.success('Question deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await questionsApi.importCSV(examId, file);
      toast.success(`Imported ${res.data.data.imported} questions`);
      if (res.data.data.errors?.length) toast.error(`${res.data.data.errors.length} rows had errors`);
      const qRes = await questionsApi.getByExam(examId);
      setQuestions(qRes.data.data || []);
    } catch (err) { toast.error(err.response?.data?.message || 'Import failed'); }
    finally { setImporting(false); e.target.value = ''; }
  };

  const downloadTemplate = () => {
    const csv = `question_text,question_type,option_a,option_b,option_c,option_d,correct_answer,explanation,category,difficulty,marks
"What is 2+2?",mcq,"2","3","4","5",c,"Basic arithmetic",Math,easy,1
"The sky is blue",true_false,,,,,true,"Sky color",Science,easy,1
"Which are even numbers? Select all",multi_correct,"2","3","4","5","a,c","Even numbers",Math,medium,2`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'question_template.csv'; a.click();
  };

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(`/admin/exams/${examId}`)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <FiArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-black text-gray-900">{exam?.title || 'Loading...'}</h1>
          <p className="text-sm text-gray-500">{questions.length} / {exam?.total_questions} questions</p>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleCSVImport} />
          <button onClick={downloadTemplate} className="btn-secondary text-sm py-2">
            <FiDownload size={14} /> Template
          </button>
          <button onClick={() => fileRef.current.click()} disabled={importing} className="btn-secondary text-sm py-2">
            {importing ? <span className="w-3 h-3 border-2 border-gray-400 border-t-gray-700 rounded-full animate-spin" /> : <FiUpload size={14} />}
            Import CSV
          </button>
          <button onClick={() => { setShowForm(true); setEditingQ(null); }} className="btn-primary text-sm py-2">
            <FiPlus size={14} /> Add Question
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="card p-4">
        <div className="flex justify-between text-sm text-gray-500 mb-2">
          <span className="font-medium">Question Progress</span>
          <span><span className="font-bold text-navy">{questions.length}</span> / {exam?.total_questions || '?'} required</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div className="bg-navy h-2 rounded-full transition-all duration-500"
            style={{ width: `${Math.min(100, ((questions.length / (exam?.total_questions || 1)) * 100))}%` }} />
        </div>
        {questions.length >= (exam?.total_questions || 0) && exam?.total_questions > 0 && (
          <p className="text-xs text-emerald-600 font-semibold mt-1">✓ Enough questions to publish this exam</p>
        )}
      </div>

      {/* Add form */}
      {showForm && !editingQ && (
        <QuestionForm onSave={handleAdd} onCancel={() => setShowForm(false)} />
      )}

      {/* Question list */}
      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 card animate-pulse" />)}</div>
      ) : questions.length === 0 && !showForm ? (
        <div className="card p-12 text-center">
          <p className="text-gray-400 font-medium mb-2">No questions yet</p>
          <p className="text-gray-400 text-sm">Add questions manually or import from CSV</p>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map((q, idx) => (
            editingQ?.id === q.id ? (
              <QuestionForm key={q.id} question={q} onSave={handleUpdate} onCancel={() => setEditingQ(null)} />
            ) : (
              <div key={q.id} className="card p-4 hover:shadow-card-hover transition-shadow">
                <div className="flex gap-3">
                  <span className="w-7 h-7 bg-navy/10 text-navy rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm leading-relaxed">{q.question_text}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${DIFF_COLORS[q.difficulty]}`}>{q.difficulty}</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{TYPE_LABELS[q.question_type]}</span>
                      {q.category && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{q.category}</span>}
                      <span className="text-xs text-gray-400">{q.marks} mark{q.marks !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-1.5">
                      {q.question_type !== 'true_false' ? (['a','b','c','d'].filter(o => q[`option_${o}`]).map(o => (
                        <div key={o} className={`text-xs px-2.5 py-1 rounded-lg flex items-center gap-1.5 ${q.correct_answer?.includes(o) ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'bg-gray-50 text-gray-600'}`}>
                          <span className="font-bold uppercase">{o}.</span> {q[`option_${o}`]}
                          {q.correct_answer?.includes(o) && <FiCheck size={10} className="ml-auto" />}
                        </div>
                      ))) : (
                        <div className="text-xs px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 font-semibold col-span-2">
                          Correct: {q.correct_answer}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setEditingQ(q)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-navy">
                      <FiEdit2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(q.id)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500">
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}
